/**
 * Raycast targets.
 *
 * Every part the user can grab carries a `HitInfo` in its `userData`, which is
 * all the gesture layer needs to know about the 3D scene.
 */

import type * as THREE from 'three';

export type HitType = 'disc' | 'arm' | 'pitch' | 'start' | 'speed' | 'power' | 'cue';

export interface HitInfo {
  type: HitType;
  /** The RPM a speed button selects. */
  value?: number;
}

export interface HitData {
  hit?: HitInfo;
}

/** Collects the meshes the raycaster should test, and tags each with its role. */
export class HitRegistry {
  readonly meshes: THREE.Mesh[] = [];

  add = (mesh: THREE.Mesh, hit: HitInfo): THREE.Mesh => {
    (mesh.userData as HitData).hit = hit;
    this.meshes.push(mesh);
    return mesh;
  };

  /**
   * The mesh registered for a hit type, if any — used to aim gestures at it.
   * A `value` picks between parts that share a type, such as 33 and 45.
   */
  find(type: HitType, value?: number): THREE.Mesh | undefined {
    return this.meshes.find((m) => {
      const hit = (m.userData as HitData).hit;
      return hit?.type === type && (value === undefined || hit.value === value);
    });
  }
}

export type AddHit = (mesh: THREE.Mesh, hit: HitInfo) => THREE.Mesh;

export const hitOf = (object: THREE.Object3D): HitInfo | null =>
  (object.userData as HitData).hit ?? null;
